import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { useEffect, useMemo, useRef, useState } from "react";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import { yupResolver } from "@hookform/resolvers/yup";
import { useDeleteDocument, useUpdateDocument, useUploadDocument } from "api/documents";
import CustomReactSelect from "components/CustomReactSelect";
import HeaderCard from "components/DataTable/HeaderCard";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useGlobalStore from "store/useGlobalStore";
import Swal from "sweetalert2";
import { formatFileSize, getStatusFilters, jsonToFormData } from "utils/helper";
import * as yup from "yup";
import { parentRoute } from ".";
import { formatDateTime } from "utils/helper";

const documentCategories = [
    { value: 1, label: 'Manual' },
    { value: 2, label: 'Plans & Drawings' },
    { value: 3, label: 'Safety Guidelines & Rules' },
    { value: 4, label: 'Risk Analysis' },
]

const documentCategoryTags = [
    { value: 1, label: 'Electrical' },
    { value: 2, label: 'Mechanical' },
]

const documentSchema = yup.object({
    do_name: yup.string().trim().required("Document name is required").max(50, "Max 50 characters"),
    do_category: yup.string().required("Category is required"),
    do_category_tag: yup.string().required("Category Tag is required"),
});

const UploadDocument = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { setPrevSelectedRow } = useGlobalStore();
    const stateItem = location.state?.item;
    const documentId = Number(location.pathname.split('/')[2]);
    const [formData, setFormData] = useState({});
    const statusSelection = getStatusFilters(false);
    const isAdd = useMemo(() =>
        location.pathname.includes(`${parentRoute}/add-document`),
        [location.pathname]
    );

    const uploadDocumentMutation = useUploadDocument();
    const updateDocumentMutation = useUpdateDocument();
    const deleteDocumentMutation = useDeleteDocument();
    const isSaving = uploadDocumentMutation.isLoading || updateDocumentMutation.isLoading || deleteDocumentMutation.isLoading;
    const documentData = stateItem;
    const {
        trigger,
        setValue,
        formState: { errors: rhfErrors },
    } = useForm({
        resolver: yupResolver(documentSchema, { context: { isAdd } }),
        mode: "onSubmit",
        defaultValues: {
            do_name: "",
            do_category: "",
            do_category_tag: "",
            do_status: "",
            do_file: null,
        },
    });

    // Sync your formData to RHF for validation
    useEffect(() => {
        Object.entries(formData).forEach(([k, v]) => setValue(k, v, { shouldValidate: false, shouldDirty: false }));
    }, [formData, setValue]);

    // Map RHF errors to your formErrors for UI
    const [formErrors, setFormErrors] = useState({});
    useEffect(() => {
        const mapped = {};
        Object.entries(rhfErrors || {}).forEach(([k, v]) => {
            mapped[k] = v?.message;
        });
        setFormErrors(mapped);
    }, [rhfErrors]);

    useEffect(() => {
        if (isAdd) return;

        if (!stateItem) {
            navigate(parentRoute, { state: location.state });
            return;
        }

        setFormData({
            do_name: stateItem.name,
            do_category: stateItem.category?.id || "",
            do_category_tag: stateItem.category_tag?.id || "",
            do_status: stateItem.status?.id || "",
        });
    }, [isAdd, location.state, navigate, stateItem]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleValidatedSubmit = async (e) => {
        e.preventDefault();
        setFormErrors({});
        const valid = await trigger();
        if (!valid) {
            toast.error("Please fix the validation errors before submitting.");
            return;
        }
        await handleSubmit(e); // your original submit logic
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isAdd && !formData.do_file) {
            toast.error("Please select a file to upload");
            return;
        }
        let passData = jsonToFormData(formData);
        try {
            if (isAdd) {
                await uploadDocumentMutation.mutateAsync(passData);
                toast.success("Document uploaded successfully");
                navigate(parentRoute, { state: location.state });
            } else {
                await updateDocumentMutation.mutateAsync({ id: documentId, data: passData });
                toast.success("Document updated successfully");
            }
        } catch (error) {
            toast.error(error?.data?.message || "Failed to upload document");
        } 
    };

    const handleDelete = async () => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes'
        });

        if (result.isConfirmed) {
            try {
                await deleteDocumentMutation.mutateAsync(documentId);
                toast.success("Document deleted successfully");
                navigate(parentRoute);
            } catch (error) {
                toast.error(error?.data?.message || 'Failed to delete document');
            }
        }
    }

    const fileInputRef = useRef();

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <HeaderCard
                                title={isAdd ? "Upload New Document" : "Edit Document"}
                                showBackButton
                                onBackClick={() => { navigate(parentRoute, { state: location.state }); setPrevSelectedRow(documentId, parentRoute) }}
                            />
                            <MDBox pt={3} px={2} pb={2}>
                                {!isAdd && (
                                    <MDBox mb={2} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
                                        <MDTypography variant="body2" color="text" gutterBottom>
                                            Created At: {stateItem?.created_at ? formatDateTime(stateItem.created_at) : '-'}
                                        </MDTypography>
                                        <MDTypography variant="body2" color="text" gutterBottom>
                                            Last Updated: {stateItem?.updated_at ? formatDateTime(stateItem.updated_at) : '-'}
                                        </MDTypography>
                                    </MDBox>
                                )}
                                <form noValidate onSubmit={handleValidatedSubmit}>
                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={isAdd ? 12 : 6}>
                                            <MDInput
                                                fullWidth
                                                label="Document name"
                                                name="do_name"
                                                type="text"
                                                required
                                                value={formData.do_name || ""}
                                                onChange={handleChange}
                                                inputProps={{ maxLength: 50 }}
                                                error={!!formErrors.do_name}
                                                helperText={formErrors.do_name}
                                            />
                                        </Grid>
                                        {!isAdd && <Grid item xs={12} md={6}>
                                            <CustomReactSelect
                                                label="Status"
                                                name="do_status"
                                                variant="outlined"
                                                value={statusSelection.find(status => status.value === formData.do_status ?? formData.do_status?.id)}
                                                onChange={(selectedOption) => handleChange({ target: { name: 'do_status', value: selectedOption?.value } })}
                                                options={statusSelection}
                                                required
                                                error={!!formErrors.do_status}
                                                helperText={formErrors.do_status}
                                            />
                                        </Grid>}
                                        <Grid item xs={12} md={6}>
                                            <CustomReactSelect
                                                label="Category"
                                                name="do_category"
                                                variant="outlined"
                                                value={documentCategories.find(role => role.value === (formData.do_category))}
                                                onChange={(selectedOption) => handleChange({ target: { name: 'do_category', value: selectedOption?.value } })}
                                                options={documentCategories}
                                                required
                                                error={!!formErrors.do_category}
                                                helperText={formErrors.do_category}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <CustomReactSelect
                                                label="Category Tag"
                                                name="do_category_tag"
                                                variant="outlined"
                                                value={documentCategoryTags.find(role => role.value === (formData.do_category_tag))}
                                                onChange={(selectedOption) => handleChange({ target: { name: 'do_category_tag', value: selectedOption?.value } })}
                                                options={documentCategoryTags}
                                                required
                                                error={!!formErrors.do_category_tag}
                                                helperText={formErrors.do_category_tag}
                                            />
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <MDTypography variant="caption">Document</MDTypography>
                                            <MDBox
                                                onClick={(e) => {
                                                    if (fileInputRef.current) {
                                                        fileInputRef.current.value = "";
                                                        fileInputRef.current.click();
                                                    }
                                                }}
                                                sx={{
                                                    flex: 1,
                                                    border: '2px dashed #ccc',
                                                    borderRadius: '8px',
                                                    p: 3,
                                                    width: "100%",
                                                    height: '125px',
                                                    textAlign: 'center',
                                                }}
                                            >
                                                <MDBox>
                                                    <MDButton
                                                        variant="contained"
                                                        color="info"
                                                        size="small"
                                                    >
                                                        Browse File
                                                    </MDButton>
                                                    <input
                                                        id="file-upload"
                                                        type="file"
                                                        hidden
                                                        required={isAdd}
                                                        accept=".doc,.docx,.xls,.xlsx,.pdf"
                                                        ref={fileInputRef}
                                                        onChange={e => {
                                                            // Only update if a file is actually selected
                                                            if (e.target.files && e.target.files[0]) {
                                                                const file = e.target.files[0];
                                                                const maxSize = 25600 * 1024; // 25600 KB in bytes

                                                                if (file.size > maxSize) {
                                                                    toast.error("File size exceeds the maximum limit of 25600 KB");
                                                                    e.target.value = "";
                                                                    return;
                                                                }

                                                                setFormData(prev => ({
                                                                    ...prev,
                                                                    do_file: file
                                                                }));
                                                            }
                                                        }}
                                                    />
                                                </MDBox>
                                                {!isAdd && documentData?.file_name ?
                                                    <MDBox mt={1}>
                                                        <MDButton
                                                            variant="contained"
                                                            color="dark"
                                                            size="small"
                                                            onClick={() => window.open(documentData?.file_name, '_blank')}
                                                        >
                                                            View File
                                                        </MDButton>
                                                    </MDBox>
                                                    :
                                                    <MDBox mt={1} color="text" fontSize={14}>
                                                        {formData.do_file ? (
                                                            <>
                                                                <b>Selected file:</b> {formData.do_file.name}
                                                                <br />
                                                                <b>Size:</b> {formatFileSize(formData.do_file.size)}
                                                            </>
                                                        ) : (
                                                            <MDTypography color="text" variant="subtitle2">* Only .doc,.docx,.xls,.xlsx,.pdf file is allowed (Max: 25 MB)</MDTypography>
                                                        )}
                                                    </MDBox>}
                                                {formErrors.do_file && (
                                                    <MDTypography color="error" variant="caption">
                                                        {formErrors.do_file}
                                                    </MDTypography>
                                                )}
                                            </MDBox>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item>
                                                    {!isAdd && <MDButton
                                                        variant="contained"
                                                        color="error"
                                                        onClick={handleDelete}
                                                        loading={isSaving}
                                                    >
                                                        Delete
                                                    </MDButton>}
                                                </Grid>
                                                <Grid item xs />
                                                <Grid item>
                                                    <MDBox display="flex" justifyContent="flex-end">
                                                        <MDButton
                                                            variant="contained"
                                                            color="primary"
                                                            type="submit"
                                                            loading={isSaving}
                                                            disabled={isAdd && !formData.do_file}
                                                        >
                                                            Submit
                                                        </MDButton>
                                                    </MDBox>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </form>
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default UploadDocument;
